import React, { createContext, useContext, useEffect, useState } from 'react';
import EventFinderAPI from '../services/api';
import type {
  ForgetPastRememberedEventsResponse,
  RememberedEventMutationResponse,
} from '../types/api';

export const BROWSER_ID_STORAGE_KEY = 'eventfinder.remembered-events.browser-id.v1';
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface BrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): unknown;
}

let memoryBrowserId = '';

export function isUuidV4(value: string | null | undefined): boolean {
  return UUID_V4_PATTERN.test(String(value || '').trim());
}

function generateUuidV4(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getOrCreateBrowserId(
  storage: BrowserStorage | null = typeof window === 'undefined' ? null : window.localStorage,
  generator: () => string = generateUuidV4,
): string {
  try {
    const stored = storage?.getItem(BROWSER_ID_STORAGE_KEY);
    if (isUuidV4(stored)) return String(stored).toLowerCase();
  } catch {
    if (isUuidV4(memoryBrowserId)) return memoryBrowserId;
  }

  const generated = generator().toLowerCase();
  if (!isUuidV4(generated)) throw new Error('Browser identity generator did not return a UUID v4');
  memoryBrowserId = generated;
  try {
    storage?.setItem(BROWSER_ID_STORAGE_KEY, generated);
  } catch {
    // The in-memory identity keeps this tab usable when browser storage is blocked.
  }
  return generated;
}

interface RememberedEventsContextValue {
  browserId: string;
  eventIds: ReadonlySet<number>;
  total: number;
  isAvailable: boolean;
  isLoading: boolean;
  pendingEventIds: ReadonlySet<number>;
  revision: number;
  error: string | null;
  isRemembered(eventId: number): boolean;
  remember(eventId: number): Promise<RememberedEventMutationResponse>;
  forget(eventId: number): Promise<RememberedEventMutationResponse>;
  forgetPast(): Promise<ForgetPastRememberedEventsResponse>;
  refresh(): Promise<void>;
}

const unavailable = async (): Promise<never> => {
  throw new Error('Remembered events are unavailable outside the public application');
};

const EMPTY_CONTEXT: RememberedEventsContextValue = {
  browserId: '',
  eventIds: new Set<number>(),
  total: 0,
  isAvailable: false,
  isLoading: false,
  pendingEventIds: new Set<number>(),
  revision: 0,
  error: null,
  isRemembered: () => false,
  remember: unavailable,
  forget: unavailable,
  forgetPast: unavailable,
  refresh: async () => {},
};

const RememberedEventsContext = createContext<RememberedEventsContextValue>(EMPTY_CONTEXT);

export const RememberedEventsProvider: React.FC<React.PropsWithChildren<{ enabled?: boolean }>> = ({
  children,
  enabled = true,
}) => {
  const [browserId] = useState(() => enabled ? getOrCreateBrowserId() : '');
  const [eventIds, setEventIds] = useState<Set<number>>(new Set());
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);
  const [pendingEventIds, setPendingEventIds] = useState<Set<number>>(new Set());
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (signal?: AbortSignal) => {
    if (!enabled || !browserId) return;
    const response = await EventFinderAPI.getRememberedEventIds(browserId, signal);
    setEventIds(new Set(response.eventIds));
    setTotal(response.total);
  };

  useEffect(() => {
    if (!enabled || !browserId) return;
    const controller = new AbortController();
    setIsLoading(true);
    refresh(controller.signal)
      .then(() => setError(null))
      .catch(loadError => {
        if ((loadError as Error).name !== 'AbortError') {
          setError((loadError as Error).message || 'Remembered events could not be loaded');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
    // Browser identity is immutable for the provider lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserId, enabled]);

  const mutate = async (eventId: number, remembered: boolean) => {
    setPendingEventIds(current => new Set(current).add(eventId));
    setError(null);
    try {
      const response = remembered
        ? await EventFinderAPI.rememberEvent(browserId, eventId)
        : await EventFinderAPI.forgetRememberedEvent(browserId, eventId);
      setEventIds(current => {
        const next = new Set(current);
        if (response.remembered) next.add(eventId);
        else next.delete(eventId);
        return next;
      });
      setTotal(response.total);
      setRevision(current => current + 1);
      return response;
    } catch (mutationError) {
      setError((mutationError as Error).message || 'My Events could not be updated');
      throw mutationError;
    } finally {
      setPendingEventIds(current => {
        const next = new Set(current);
        next.delete(eventId);
        return next;
      });
    }
  };

  const forgetPast = async () => {
    const response = await EventFinderAPI.forgetPastRememberedEvents(browserId);
    setTotal(response.total);
    await refresh();
    setRevision(current => current + 1);
    return response;
  };

  const value: RememberedEventsContextValue = {
    browserId,
    eventIds,
    total,
    isAvailable: enabled,
    isLoading,
    pendingEventIds,
    revision,
    error,
    isRemembered: eventId => eventIds.has(eventId),
    remember: eventId => mutate(eventId, true),
    forget: eventId => mutate(eventId, false),
    forgetPast,
    refresh: () => refresh(),
  };

  return <RememberedEventsContext.Provider value={value}>{children}</RememberedEventsContext.Provider>;
};

export function useRememberedEvents(): RememberedEventsContextValue {
  return useContext(RememberedEventsContext);
}

export default RememberedEventsContext;